import { fileUrl } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { CaretUp, CaretDown, X } from "@phosphor-icons/react";

/**
 * OrgChart — hierarchical role chart for org/church/cultural circles.
 *
 * Builds tiers from placeholder nodes using `parent_user_id` /
 * `parent_placeholder_id` as the parent pointer. Nodes without a parent live
 * at tier 0. Members are NOT included here — the org chart is built strictly
 * from placeholder seats (multi-role: same user_id can hold several seats).
 *
 * Memorials of any seat-holder surface in the same chair, marked with a
 * silver "in memoriam" indicator so the org sees who has passed.
 *
 * `onSelect(node)` — open the detail dialog. `onClaim(node)` — claim an
 * unclaimed seat. `onReorder(node, direction)` — owner-only nudge up/down
 * within the same parent group. `onDelete(node)` — owner-only remove.
 */
export default function OrgChart({ nodes, ownerControls = false, onSelect, onClaim, onReorder, onDelete }) {
  // Build a tree from placeholder nodes alone.
  const placeholders = (nodes || []).filter((n) => n.is_placeholder || n.member_type === "org_role");
  if (placeholders.length === 0) {
    return (
      <div className="legacy-card p-10 text-center text-sageGray" data-testid="orgchart-empty">
        No roles defined yet. Add a seat from <em>Circle settings → Placeholders</em>.
      </div>
    );
  }
  const byParent = new Map();
  for (const p of placeholders) {
    const key = p.parent_placeholder_id || p.parent_user_id || "__root__";
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(p);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => (a.position_order || 0) - (b.position_order || 0) || (a.name || "").localeCompare(b.name || ""));
  }

  const renderRow = (parentKey, depth) => {
    const list = byParent.get(parentKey) || [];
    if (list.length === 0) return null;
    return (
      <div className="flex flex-wrap justify-center gap-1.5" data-testid={`orgchart-tier-${depth}`}>
        {list.map((n, idx) => (
          <div key={n.id} className="flex flex-col items-center" style={{ minWidth: 132 }}>
            <Card
              node={n}
              ownerControls={ownerControls}
              first={idx === 0}
              last={idx === list.length - 1}
              onSelect={onSelect}
              onClaim={onClaim}
              onReorder={onReorder}
              onDelete={onDelete}
            />
            {byParent.has(n.id) && (
              <div className="mt-2 pt-2 border-t-2 border-forest/20 w-full flex justify-center">
                {renderRow(n.id, depth + 1)}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="legacy-card p-4 overflow-x-auto" data-testid="orgchart">
      <div className="min-w-fit">
        {renderRow("__root__", 0)}
      </div>
    </div>
  );
}

function Card({ node, ownerControls, first, last, onSelect, onClaim, onReorder, onDelete }) {
  const photo = node.profile_photo_path || node.photo_path;
  const claimed = !!node.is_claimed;
  const initial = (node.name || "?").charAt(0).toUpperCase();
  return (
    <div
      className={`relative w-36 rounded-xl border-2 ${claimed ? "border-forest/40 bg-white" : "border-dashed border-[#E89C3F] bg-[#FFF8EC]"} px-3 py-2.5 cursor-pointer transition-shadow hover:shadow-md`}
      onClick={() => onSelect?.(node)}
      data-testid={`orgchart-card-${node.id}`}
    >
      <div className="flex flex-col items-center gap-1.5">
        <div className={`w-12 h-12 rounded-full overflow-hidden flex items-center justify-center ${claimed ? "bg-forest text-gold" : "bg-cream text-sageGray border-2 border-dashed border-[#E89C3F]"}`}>
          {photo ? (
            <img src={fileUrl(photo)} alt={node.name} className="w-full h-full object-cover" />
          ) : (
            <span className="font-display text-lg">{initial}</span>
          )}
        </div>
        <p className="font-display text-sm text-forest text-center leading-tight">{node.name}</p>
        {node.role_or_relationship && (
          <p className="text-[10px] uppercase tracking-widest text-sageGray text-center leading-tight">{node.role_or_relationship}</p>
        )}
        {!claimed && (
          <Badge className="bg-[#FFF6E5] text-[#8A5A1F] border border-[#E89C3F]/40 rounded-full text-[9px] uppercase tracking-widest">
            Open seat
          </Badge>
        )}
      </div>
      {ownerControls && (
        <div className="absolute -top-2 right-1 flex gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onReorder?.(node, -1)}
            disabled={first}
            className="w-6 h-6 rounded-full bg-white border border-sage text-forest hover:bg-cream disabled:opacity-40 flex items-center justify-center"
            data-testid={`orgchart-up-${node.id}`}
            title="Move up"
          >
            <CaretUp size={12} weight="bold" />
          </button>
          <button
            onClick={() => onReorder?.(node, 1)}
            disabled={last}
            className="w-6 h-6 rounded-full bg-white border border-sage text-forest hover:bg-cream disabled:opacity-40 flex items-center justify-center"
            data-testid={`orgchart-down-${node.id}`}
            title="Move down"
          >
            <CaretDown size={12} weight="bold" />
          </button>
          <button
            onClick={() => onDelete?.(node)}
            className="w-6 h-6 rounded-full bg-white border border-sage text-red-600 hover:bg-red-50 flex items-center justify-center"
            data-testid={`orgchart-delete-${node.id}`}
            title="Delete seat"
          >
            <X size={12} weight="bold" />
          </button>
        </div>
      )}
      {!claimed && onClaim && (
        <button
          onClick={(e) => { e.stopPropagation(); onClaim(node); }}
          className="mt-3 w-full text-[11px] uppercase tracking-widest text-[#8A5A1F] underline hover:text-forest"
          data-testid={`orgchart-claim-${node.id}`}
        >
          Claim this seat
        </button>
      )}
    </div>
  );
}
